import { initLazyScript } from '../utils/theme-functions.js';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';

/**
 * PhotoSwipe Gallery Component
 * Initializes PhotoSwipe lightbox for image galleries
 *
 * Usage in Liquid:
 * <photoswipe-gallery>
 *   <a href="image-large.jpg"
 *      class="pswp-image"
 *      data-pswp-width="1200"
 *      data-pswp-height="800">
 *     <img src="image-thumb.jpg" alt="">
 *   </a>
 * </photoswipe-gallery>
 *
 * For videos, use:
 * <a href="video-url"
 *    class="pswp-video"
 *    data-pswp-type="video">
 */
class PhotoswipeGallery extends HTMLElement {
  constructor() {
    super();
    initLazyScript(this, this.init.bind(this));
  }

  init() {
    const gallerySelector = this.dataset.gallery || this;

    this.lightbox = new PhotoSwipeLightbox({
      gallery: gallerySelector,
      children: 'a.pswp-image, a.pswp-video',
      pswpModule: PhotoSwipe,

      // UI options
      bgOpacity: 0.9,
      spacing: 0.12,
      allowPanToNext: true,
      zoom: true,
      close: true,
      arrowKeys: true,

      // Enable zoom on images
      imageClickAction: 'zoom',
      tapAction: 'close',
      doubleTapAction: 'zoom',

      // Custom options
      paddingFn: (viewportSize) => {
        return {
          top: 30,
          bottom: 30,
          left: 70,
          right: 70
        };
      }
    });

    // Handle video items - create HTML5 video element
    this.lightbox.on('contentLoad', (e) => {
      const { content } = e;

      if (content.type === 'video' || content.element?.classList.contains('pswp-video')) {
        e.preventDefault();

        const videoUrl = content.data.src;
        if (videoUrl) {
          // Create video element
          content.element = document.createElement('div');
          content.element.className = 'pswp__video-container';
          content.element.innerHTML = `
            <video controls style="width: 100%; height: 100%; object-fit: contain;">
              <source src="${videoUrl}" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          `;
        }
      }
    });

    // Initialize the lightbox
    this.lightbox.init();
  }

  disconnectedCallback() {
    if (this.lightbox) {
      this.lightbox.destroy();
    }
  }
}

customElements.define('photoswipe-gallery', PhotoswipeGallery);

export { PhotoswipeGallery };
